/**
 * 桶码提示条（P1-1.7）
 *
 * 一行代码只做一件事：把服务端给的 code 变成一条人能读的提示。
 * 顺序按服务端给的顺序画（同层互斥、跨层共存已由服务端排好），前端不重排、不合并、不判优先级。
 */
import React from 'react';
import PropTypes from 'prop-types';
import { BUCKET_NOTICES, noticeFor } from './registry';
import { NOTICE_COPY, noticeText } from './copy';
import styles from './index.less';

// tone → 类名静态映射：CSS Modules 下 `styles[`tone_${tone}`]` 这种拼键在产物里可能取不到，
// 取不到就是"提示条没有告警色"，页面上看不出来，只会安静地降级成一条不起眼的话。
const TONE_CLASS = {
  warning: styles.tone_warning,
  info: styles.tone_info,
  muted: styles.tone_muted,
};

/**
 * @param {object} props
 * @param {string} props.code 桶码
 * @param {object} props.ctx 文案上下文（pageContext + contextOf 合并后的占位符字典）
 */
export const Notice = React.memo(function Notice({ code, ctx }) {
  const notice = noticeFor(code);
  if (!notice) return null; // 未登记码由调用方收集并上报，这里静默跳过
  // 可选字段缺值 ⇒ 不画这一条，而不是抛错弄碎整页：提示条是装饰，不是数据
  const body = noticeText(code, ctx);
  if (!body) return null;
  return (
    <div
      className={`${styles.notice} ${TONE_CLASS[notice.tone] || ''}`}
      data-test="hh-v2-notice"
      data-code={code}
    >
      <span className={styles.noticeTitle}>{notice.title}</span>
      <span className={styles.noticeBody}>{body}</span>
    </div>
  );
});

/**
 * 一维/整页的提示条集合。`buckets` 缺失或为空时什么都不画（空数组是正常形态：
 * 表示"这一维没有任何判定"，不是数据丢了 —— 真丢了会由 guard 报出来）。
 */
export const NoticeList = React.memo(function NoticeList({ buckets, ctx }) {
  const list = Array.isArray(buckets) ? buckets : [];
  if (!list.length) return null;
  return (
    <div className={styles.noticeList}>
      {list.map((code) => (
        <Notice key={code} code={code} ctx={ctx} />
      ))}
    </div>
  );
});

/** 供测试与 1.8 的构建期校验用：每个已登记码都得有模板与 tone，否则页面会安静地少一条提示。 */
export function missingTemplates() {
  const noCopy = Object.keys(BUCKET_NOTICES).filter(
    (code) => !NOTICE_COPY[BUCKET_NOTICES[code].copyKey],
  );
  const noStyle = Object.keys(BUCKET_NOTICES).filter(
    (code) =>
      !BUCKET_NOTICES[code].title || !TONE_CLASS[BUCKET_NOTICES[code].tone],
  );
  return noCopy.concat(noStyle);
}

Notice.propTypes = {
  code: PropTypes.string.isRequired,
  // ctx 是开放的占位符字典（pageContext + contextOf 的并集）：在这里钉死键集，
  // 会把"新码要一个新占位符"变成一次 props 改造，而它本来只需要改模板 + 补上下文。
  ctx: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

NoticeList.propTypes = {
  buckets: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  ctx: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default Notice;
